import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, LineElement, PointElement } from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import projectService from '../services/projectService';
import actionService from '../services/actionService';
import targetService from '../services/targetService';
import exportService from '../services/exportService';
import webFindingService from '../services/webFindingService';
import blueTeamService from '../services/blueTeamService';
import webTargetService from '../services/webTargetService';
import enhancedDebriefGeneratorService from '../services/enhancedDebriefGeneratorService';
import debriefExportService from '../services/debriefExportService';
import reportTemplatesService from '../services/reportTemplatesService';
import reportChartService from '../services/reportChartService';
import MitreHeatmap from '../components/mitre/MitreHeatmap';
import ActionDetailModal from '../components/common/ActionDetailModal';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faClipboardList,
  faBullseye,
  faCheckCircle,
  faTrophy,
  faMapMarkedAlt,
  faChartLine,
  faClock,
  faDownload,
  faFilePdf,
  faFileExcel,
  faMap,
  faChartBar,
  faUser,
  faCalendarAlt,
  faSpinner,
  faChartPie,
  faChartArea,
  faAward,
  faFileAlt
} from '@fortawesome/free-solid-svg-icons';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, LineElement, PointElement);

const ProjectReports = () => {
  const { projectId } = useParams();
  const { t } = useLanguage();
  const { darkMode: isDarkMode } = useTheme();
  const [project, setProject] = useState(null);
  const [actions, setActions] = useState([]);
  const [targets, setTargets] = useState([]);
  const [webFindingsForMitre, setWebFindingsForMitre] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedAction, setSelectedAction] = useState(null);
  const [debrief, setDebrief] = useState(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [reportTheme, setReportTheme] = useState('dark');

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const [projectData, actionsData, targetsData] = await Promise.all([
        projectService.getProject(projectId),
        actionService.getActionsByProject(projectId),
        targetService.getTargetsByProject(projectId)
      ]);

      setProject(projectData);
      setActions(Array.isArray(actionsData) ? actionsData : []);
      setTargets(Array.isArray(targetsData) ? targetsData : []);

      // Fetch web findings for MITRE overlay (silent fail)
      try {
        const wfData = await webFindingService.getAll({ project: projectId });
        const findings = Array.isArray(wfData) ? wfData : (wfData?.data || []);
        if (findings.length > 0) {
          const mapped = findings
            .filter(f => f.mitreTechnique?.id)
            .map(f => ({
              _id: f._id,
              title: f.title,
              severity: f.severity,
              wstgTestId: f.wstgTest?.testId,
              techniqueId: f.mitreTechnique?.id,
              techniqueName: f.mitreTechnique?.name,
              tactic: f.mitreTechnique?.tactic
            }));
          setWebFindingsForMitre(mapped);
        }
      } catch (e) { /* no web findings */ }

      // Generate debrief for template exports (silent fail)
      try {
        const blueTeamStats = await blueTeamService.getProjectStats(projectId).catch(() => null);
        let webData = null;
        try {
          const webTargets = await webTargetService.getAll(projectId);
          if (webTargets && webTargets.length > 0) {
            const wf = await webFindingService.getAll({ project: projectId });
            webData = { webTargets, webFindings: Array.isArray(wf) ? wf : [] };
          }
        } catch { /* web data not available */ }
        const actionsArr = Array.isArray(actionsData) ? actionsData : [];
        const targetsArr = Array.isArray(targetsData) ? targetsData : [];
        const generatedDebrief = enhancedDebriefGeneratorService.generateDebrief(
          projectData, actionsArr, targetsArr, [], blueTeamStats, webData
        );
        setDebrief(generatedDebrief);
      } catch (e) { console.error('Debrief generation failed:', e); }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching project data:', error);
      setError('Failed to load project data');
      setLoading(false);
    }
  };

  // Export handlers
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      await exportService.exportExecutiveSummary(projectId);
      toast.success(t('reports.downloadReady'));
    } catch (error) {
      toast.error('Failed to export executive summary');
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await exportService.exportExcel(projectId);
      toast.success(t('reports.downloadReady'));
    } catch (error) {
      toast.error('Failed to export Excel data');
    } finally {
      setExporting(false);
    }
  };

  const handleExportMitreNavigator = async () => {
    setExporting(true);
    try {
      await exportService.exportMitreNavigator(projectId, actions, project.name);
      toast.success(t('reports.downloadReady'));
    } catch (error) {
      console.error('MITRE Navigator export error:', error);
      toast.error(`Failed to export MITRE Navigator layer: ${error.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  const handleExportAnalytics = async () => {
    setExporting(true);
    try {
      await exportService.exportAnalytics(projectId);
      toast.success(t('reports.downloadReady'));
    } catch (error) {
      toast.error('Failed to export analytics dashboard');
    } finally {
      setExporting(false);
    }
  };

  // Template export handler
  const handleTemplateExport = async (templateId) => {
    if (!debrief || !project) return;

    setExporting(true);
    setShowTemplateModal(false);

    try {
      let reportData;
      const timestamp = new Date().toISOString().split('T')[0];
      const baseFilename = `${templateId}-${project.name.replace(/\s+/g, '-')}-${timestamp}`;

      switch (templateId) {
        case 'executive-summary':
          reportData = reportTemplatesService.generateExecutiveSummary(debrief, project);
          break;
        case 'technical-detailed':
          reportData = reportTemplatesService.generateTechnicalReport(debrief, project);
          break;
        case 'compliance-audit':
          reportData = reportTemplatesService.generateComplianceReport(debrief, project, 'NIST');
          break;
        case 'incident-response':
          reportData = reportTemplatesService.generateIncidentResponseReport(debrief, project);
          break;
        case 'risk-assessment':
          reportData = reportTemplatesService.generateRiskAssessmentReport(debrief, project);
          break;
        case 'board-presentation':
          reportData = reportTemplatesService.generateBoardPresentation(debrief, project, { theme: reportTheme });
          break;
        default:
          throw new Error(`Unknown template: ${templateId}`);
      }

      const theme = templateId === 'board-presentation' ? reportTheme : 'light';
      const content = generateReportContent(reportData, templateId, theme);
      const blob = new Blob([content], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${baseFilename}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${reportData.title} exported successfully!`);
    } catch (error) {
      console.error('Template export error:', error);
      toast.error(`Failed to export report: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Handle full debrief HTML export
  const handleExportDebriefHTML = async () => {
    if (!debrief) return;
    setExporting(true);
    try {
      await debriefExportService.exportDebrief(debrief, 'html', project.name);
      toast.success(t('reports.downloadReady'));
    } catch (error) {
      console.error('Debrief HTML export error:', error);
      toast.error('Failed to export debrief');
    } finally {
      setExporting(false);
    }
  };

  // Format badge for severity/priority/risk values
  const formatBadge = (value) => {
    if (typeof value !== 'string') return value;
    const lower = value.toLowerCase();
    const badgeStyles = {
      critical: 'background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;',
      immediate: 'background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;',
      high: 'background:#fed7aa;color:#9a3412;border:1px solid #fdba74;',
      medium: 'background:#fef3c7;color:#92400e;border:1px solid #fde68a;',
      low: 'background:#d1fae5;color:#065f46;border:1px solid #6ee7b7;',
      'non-compliant': 'background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;',
      partial: 'background:#fef3c7;color:#92400e;border:1px solid #fde68a;',
      compliant: 'background:#d1fae5;color:#065f46;border:1px solid #6ee7b7;',
      pending: 'background:#fef3c7;color:#92400e;border:1px solid #fde68a;'
    };
    const style = badgeStyles[lower];
    if (style) return `<span style="${style}padding:2px 8px;border-radius:9999px;font-size:0.75rem;font-weight:600;white-space:nowrap;">${value}</span>`;
    return value;
  };

  const isBadgeColumn = (key) => {
    return ['severity', 'priority', 'status', 'risk', 'currentState', 'currentstate', 'treatment'].includes(key.toLowerCase());
  };

  // Format section content based on type
  const formatSectionContent = (contentData) => {
    if (!contentData) return '';
    if (typeof contentData === 'string') return `<p>${contentData.replace(/\n/g, '</p><p>')}</p>`;

    if (Array.isArray(contentData)) {
      if (contentData.length === 0) return '<p>No data available</p>';
      if (typeof contentData[0] === 'object') {
        const keys = Object.keys(contentData[0]);
        let html = '<table><thead><tr>';
        keys.forEach(key => { html += `<th>${key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}</th>`; });
        html += '</tr></thead><tbody>';
        contentData.forEach(item => {
          html += '<tr>';
          keys.forEach(key => {
            let value = item[key];
            if (typeof value === 'object' && value !== null) value = JSON.stringify(value);
            html += `<td>${isBadgeColumn(key) && typeof value === 'string' ? formatBadge(value) : (value || '-')}</td>`;
          });
          html += '</tr>';
        });
        html += '</tbody></table>';
        return html;
      }
      return `<ul>${contentData.map(item => `<li>${item}</li>`).join('')}</ul>`;
    }

    if (typeof contentData === 'object') {
      if (contentData._svgContent) {
        const summaryHtml = contentData.summary ? `<p style="margin-bottom:1rem;color:#6b7280;">${contentData.summary}</p>` : '';
        return `${summaryHtml}<div class="svg-wrapper" style="overflow-x:auto;"><button class="fs-btn">&#x26F6; Fullscreen</button>${contentData._svgContent}</div>`;
      }
      if (contentData._chartType === 'bar-chart-js' || (contentData.labels && contentData.datasets)) {
        return reportChartService.chartJsToSvg(contentData, { width: 500, height: 300 });
      }
      if (contentData.score !== undefined && contentData.rating !== undefined) {
        const gaugeScore = contentData.detectionRate || 0;
        const gaugeSvg = reportChartService.generateSecurityPostureGauge(contentData.score, gaugeScore);
        return `<div style="text-align:center;margin-bottom:1.5rem;">${gaugeSvg}</div>
          <div class="metric-grid">
            <div class="metric-card"><div class="metric-value">${contentData.score}</div><div class="metric-label">Security Grade</div></div>
            <div class="metric-card"><div class="metric-value">${contentData.rating}</div><div class="metric-label">Rating</div></div>
            <div class="metric-card"><div class="metric-value">${contentData.detectionRate}%</div><div class="metric-label">Detection Rate</div></div>
          </div>`;
      }
      if (contentData.critical !== undefined || contentData.high !== undefined) {
        const segments = [
          { label: 'Critical', value: contentData.critical?.length || contentData.critical || 0, color: '#dc2626' },
          { label: 'High', value: contentData.high?.length || contentData.high || 0, color: '#ea580c' },
          { label: 'Medium', value: contentData.medium?.length || contentData.medium || 0, color: '#f59e0b' },
          { label: 'Low', value: contentData.low?.length || contentData.low || 0, color: '#22c55e' }
        ].filter(s => s.value > 0);
        const donutSvg = segments.length > 0 ? reportChartService.generateDonutChart(segments, { width: 280, height: 280, title: 'Findings Distribution' }) : '';
        return `${donutSvg ? `<div style="text-align:center;margin-bottom:1.5rem;">${donutSvg}</div>` : ''}
          <div class="metric-grid">
            <div class="metric-card severity-critical"><div class="metric-value">${contentData.critical?.length || contentData.critical || 0}</div><div class="metric-label">Critical</div></div>
            <div class="metric-card severity-high"><div class="metric-value">${contentData.high?.length || contentData.high || 0}</div><div class="metric-label">High</div></div>
            <div class="metric-card severity-medium"><div class="metric-value">${contentData.medium?.length || contentData.medium || 0}</div><div class="metric-label">Medium</div></div>
            <div class="metric-card severity-low"><div class="metric-value">${contentData.low?.length || contentData.low || 0}</div><div class="metric-label">Low</div></div>
          </div>`;
      }
      if (contentData.axes && contentData.risks) {
        const risks = contentData.risks || [];
        let html = `<table><thead><tr><th>Risk</th><th>Likelihood</th><th>Impact</th><th>Severity</th></tr></thead><tbody>`;
        risks.forEach(r => { html += `<tr><td>${r.name}</td><td>${r.likelihood}/5</td><td>${r.impact}/5</td><td>${formatBadge(r.severity)}</td></tr>`; });
        html += '</tbody></table>';
        return html;
      }
      let html = '<dl>';
      Object.entries(contentData).forEach(([key, value]) => {
        if (key.startsWith('_')) return;
        const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
        let displayValue = value;
        if (Array.isArray(value)) {
          displayValue = value.length > 0 && typeof value[0] === 'object' ? formatSectionContent(value) : value.join(', ');
        } else if (typeof value === 'object' && value !== null) {
          displayValue = formatSectionContent(value);
        }
        if (isBadgeColumn(key) && typeof displayValue === 'string') displayValue = formatBadge(displayValue);
        html += `<dt><strong>${label}:</strong></dt><dd>${displayValue}</dd>`;
      });
      html += '</dl>';
      return html;
    }
    return String(contentData);
  };

  // Format slide content for board presentation
  const formatSlideContent = (slide) => {
    if (!slide.content) return '';
    switch (slide.type) {
      case 'title':
        return `<div style="text-align:center;padding:4rem;"><h2>${slide.content.headline || ''}</h2><p style="font-size:1.25rem;color:#6b7280;">${slide.content.period || ''}</p>${slide.content.classification ? `<div class="classification">${slide.content.classification}</div>` : ''}</div>`;
      case 'metrics':
        return `<div class="metric-grid">${slide.content.map(m => `<div class="metric-card"><div class="metric-value" style="color:${m.trend === 'positive' ? '#10b981' : m.trend === 'negative' ? '#ef4444' : '#2563eb'}">${m.value}</div><div class="metric-label">${m.label}</div></div>`).join('')}</div>`;
      case 'scorecard':
        const gradeColor = slide.content.overallGrade === 'A' ? '#10b981' : slide.content.overallGrade === 'B' ? '#3b82f6' : slide.content.overallGrade === 'C' ? '#f59e0b' : '#ef4444';
        const impactBullets = (slide.content.businessImpact || []).map(b => `<li style="margin:0.75rem 0;"><strong>${b.type}:</strong> ${b.text}</li>`).join('');
        return `<div style="text-align:center;"><div style="font-size:6rem;font-weight:bold;color:${gradeColor};">${slide.content.overallGrade}</div><div style="font-size:1.5rem;color:#6b7280;">${slide.content.rating}</div></div>${impactBullets ? `<div style="margin-top:2rem;"><h3>Business Impact</h3><ul style="font-size:1.1rem;text-align:left;">${impactBullets}</ul></div>` : ''}`;
      case 'list':
        return `<ul style="font-size:1.25rem;">${slide.content.map(item => `<li style="margin:1rem 0;${item.severity === 'Critical' ? 'color:#dc2626;' : ''}"><strong>${item.severity || ''}:</strong> ${item.description}</li>`).join('')}</ul>`;
      case 'action-items':
        return `<table><thead><tr><th>Action</th><th>Owner</th><th>Deadline</th><th>Status</th></tr></thead><tbody>${slide.content.map(item => `<tr><td>${item.action}</td><td>${item.owner}</td><td>${item.deadline}</td><td>${item.status}</td></tr>`).join('')}</tbody></table>`;
      case 'table':
        if (!slide.content || slide.content.length === 0) return '<p>No data</p>';
        const keys = Object.keys(slide.content[0] || {});
        return `<table><thead><tr>${keys.map(k => `<th>${k.charAt(0).toUpperCase() + k.slice(1)}</th>`).join('')}</tr></thead><tbody>${slide.content.map(row => `<tr>${keys.map(k => { const val = row[k] || ''; return `<td>${isBadgeColumn(k) ? formatBadge(val) : val}</td>`; }).join('')}</tr>`).join('')}</tbody></table>`;
      case 'chart':
        if (slide.content && (slide.content._chartType === 'bar-chart-js' || (slide.content.labels && slide.content.datasets))) {
          return `<div style="text-align:center;">${reportChartService.chartJsToSvg(slide.content, { width: 600, height: 350 })}</div>`;
        }
        return formatSectionContent(slide.content);
      case 'heatmap':
        if (slide.content?._svgContent) {
          const summaryHtml = slide.content.summary ? `<p style="margin-bottom:1rem;color:#6b7280;text-align:center;">${slide.content.summary}</p>` : '';
          return `${summaryHtml}<div class="svg-wrapper" style="overflow-x:auto;"><button class="fs-btn">&#x26F6; Fullscreen</button>${slide.content._svgContent}</div>`;
        }
        return formatSectionContent(slide.content);
      default:
        return formatSectionContent(slide.content);
    }
  };

  // Generate formatted HTML content from report data
  const generateReportContent = (reportData, templateId, theme = 'light') => {
    const isDark = theme === 'dark';
    const lightStyles = `body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;margin:0;padding:2rem;background:#f5f5f5;color:#333;line-height:1.6}.container{max-width:1000px;margin:0 auto;background:white;padding:3rem;box-shadow:0 0 20px rgba(0,0,0,0.1)}h1{color:#1f2937;border-bottom:3px solid #2563eb;padding-bottom:1rem}h2{color:#374151;margin-top:2rem;border-bottom:1px solid #e5e7eb;padding-bottom:0.5rem}h3{color:#4b5563}.header{text-align:center;margin-bottom:2rem}.classification{background:#dc2626;color:white;padding:0.5rem 1rem;display:inline-block;font-weight:bold;border-radius:4px}.subtitle{color:#6b7280;font-size:1.25rem}.section{margin:2rem 0;padding:1.5rem;background:#f9fafb;border-radius:8px}.metric-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:1rem;margin:1rem 0}.metric-card{background:white;padding:1.5rem;border-radius:8px;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,0.1)}.metric-value{font-size:2rem;font-weight:bold;color:#2563eb}.metric-label{color:#6b7280;font-size:0.875rem}table{width:100%;border-collapse:collapse;margin:1rem 0}th,td{border:1px solid #e5e7eb;padding:0.75rem;text-align:left}th{background:#f3f4f6;font-weight:600}.severity-critical{background:#fee2e2;color:#991b1b}.severity-high{background:#fed7aa;color:#9a3412}.severity-medium{background:#fef3c7;color:#92400e}.severity-low{background:#d1fae5;color:#065f46}.finding{margin:1rem 0;padding:1rem;border-radius:8px;border-left:4px solid}.finding.critical{background:#fee2e2;border-color:#dc2626}.finding.high{background:#fed7aa;border-color:#ea580c}.slide{page-break-after:always;min-height:600px;padding:2rem;border:1px solid #e5e7eb;margin:2rem 0;background:white}.slide-title{font-size:2rem;color:#1f2937;text-align:center;margin-bottom:2rem}@media print{.container{box-shadow:none}}`;
    const darkStyles = `body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;margin:0;padding:2rem;background:#0f0f1a;color:#e2e8f0;line-height:1.6}.container{max-width:1000px;margin:0 auto;background:#1a1a2e;padding:3rem;box-shadow:0 0 40px rgba(0,0,0,0.5);border-radius:12px}h1{color:#f1f5f9;border-bottom:3px solid;border-image:linear-gradient(90deg,#6366f1,#8b5cf6,#a855f7) 1;padding-bottom:1rem}h2{color:#e2e8f0;margin-top:2rem;border-bottom:1px solid #334155;padding-bottom:0.5rem}h3{color:#cbd5e1}.header{text-align:center;margin-bottom:2rem}.classification{background:linear-gradient(135deg,#dc2626,#b91c1c);color:white;padding:0.5rem 1rem;display:inline-block;font-weight:bold;border-radius:6px;letter-spacing:0.05em}.subtitle{color:#94a3b8;font-size:1.25rem}.section{margin:2rem 0;padding:1.5rem;background:#16213e;border-radius:12px;border:1px solid #1e3a5f}.metric-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:1rem;margin:1rem 0}.metric-card{background:linear-gradient(135deg,#1e293b,#0f172a);padding:1.5rem;border-radius:12px;text-align:center;border:1px solid #334155;box-shadow:0 4px 12px rgba(0,0,0,0.3)}.metric-value{font-size:2.5rem;font-weight:bold;background:linear-gradient(135deg,#60a5fa,#a78bfa);-webkit-background-clip:text;-webkit-text-fill-color:transparent}.metric-label{color:#94a3b8;font-size:0.875rem}table{width:100%;border-collapse:collapse;margin:1rem 0}th,td{border:1px solid #334155;padding:0.75rem;text-align:left}th{background:#0f172a;font-weight:600;color:#94a3b8;text-transform:uppercase;font-size:0.75rem;letter-spacing:0.05em}td{color:#cbd5e1}.severity-critical{background:rgba(220,38,38,0.15);color:#fca5a5}.severity-high{background:rgba(234,88,12,0.15);color:#fdba74}.severity-medium{background:rgba(245,158,11,0.15);color:#fde68a}.severity-low{background:rgba(34,197,94,0.15);color:#86efac}.finding{margin:1rem 0;padding:1rem;border-radius:12px;border-left:4px solid}.finding.critical{background:rgba(220,38,38,0.1);border-color:#dc2626}.finding.high{background:rgba(234,88,12,0.1);border-color:#ea580c}.slide{page-break-after:always;min-height:600px;padding:3rem;border:1px solid #1e3a5f;margin:2rem 0;background:linear-gradient(180deg,#1a1a2e,#16213e);border-radius:12px}.slide-title{font-size:2rem;text-align:center;margin-bottom:2rem;background:linear-gradient(135deg,#60a5fa,#c084fc);-webkit-background-clip:text;-webkit-text-fill-color:transparent;font-weight:700}p{color:#cbd5e1}ul,ol{color:#cbd5e1}li{margin:0.5rem 0}@media print{.container{box-shadow:none}body{background:#1a1a2e}}`;
    const styles = isDark ? darkStyles : lightStyles;
    const fullscreenStyles = `.svg-wrapper{position:relative}.svg-wrapper .fs-btn{position:absolute;top:8px;right:8px;background:${isDark ? 'rgba(30,41,59,0.85)' : 'rgba(255,255,255,0.9)'};border:1px solid ${isDark ? '#475569' : '#d1d5db'};color:${isDark ? '#e2e8f0' : '#374151'};padding:4px 10px;border-radius:6px;cursor:pointer;font-size:12px;z-index:10;opacity:0;transition:opacity .2s}.svg-wrapper:hover .fs-btn{opacity:1}.svg-wrapper .fs-btn:hover{background:${isDark ? '#334155' : '#e5e7eb'}}.fs-overlay{display:none;position:fixed;top:0;left:0;width:100vw;height:100vh;background:${isDark ? 'rgba(15,15,26,0.95)' : 'rgba(0,0,0,0.85)'};z-index:9999;justify-content:center;align-items:center;flex-direction:column;cursor:pointer}.fs-overlay.active{display:flex}.fs-overlay svg{max-width:95vw;max-height:90vh;width:auto;height:auto}.fs-overlay .fs-close{position:absolute;top:20px;right:30px;color:white;font-size:28px;cursor:pointer;background:rgba(0,0,0,0.4);border:none;border-radius:50%;width:40px;height:40px;display:flex;align-items:center;justify-content:center}@media print{.fs-btn{display:none!important}}`;
    const fullscreenScript = `<script>document.addEventListener('click',function(e){if(e.target.classList.contains('fs-btn')){var w=e.target.closest('.svg-wrapper');var svg=w.querySelector('svg');if(svg){var o=document.createElement('div');o.className='fs-overlay active';o.innerHTML='<button class="fs-close">&times;</button>'+svg.outerHTML;document.body.appendChild(o);initKcTooltips(o);o.addEventListener('click',function(ev){if(ev.target===o||ev.target.classList.contains('fs-close')){o.remove()}})}}if(e.target.classList.contains('fs-overlay')){e.target.remove()}});document.addEventListener('keydown',function(e){if(e.key==='Escape'){var o=document.querySelector('.fs-overlay');if(o)o.remove()}});function initKcTooltips(root){(root||document).querySelectorAll('.kc-node').forEach(function(node){var id=node.getAttribute('data-node');var tip=(root||document).querySelector('.kc-tip[data-for="'+id+'"]');if(!tip)return;node.addEventListener('mouseenter',function(){tip.style.opacity='1'});node.addEventListener('mouseleave',function(){tip.style.opacity='0'})})}initKcTooltips();<\/script>`;

    let content = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${reportData.title}</title><style>${styles}${fullscreenStyles}</style></head><body><div class="container">`;
    content += `<div class="header"><h1>${reportData.title}</h1>`;
    if (templateId !== 'board-presentation') {
      if (reportData.subtitle) content += `<div class="subtitle">${reportData.subtitle}</div>`;
      if (reportData.classification) content += `<div class="classification">${reportData.classification}</div>`;
      content += `<p>Generated: ${new Date(reportData.date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>`;
    }
    content += `</div>`;

    if (templateId === 'board-presentation' && reportData.slides) {
      reportData.slides.forEach(slide => {
        content += `<div class="slide"><div class="slide-title">${slide.title}</div>${formatSlideContent(slide)}</div>`;
      });
    } else if (reportData.sections) {
      reportData.sections.forEach(section => {
        content += `<div class="section"><h2>${section.title}</h2>`;
        if (section.subsections) {
          section.subsections.forEach(sub => { content += `<h3>${sub.title}</h3>${formatSectionContent(sub.content)}`; });
        } else {
          content += formatSectionContent(section.content);
        }
        content += `</div>`;
      });
    }

    content += `</div>${fullscreenScript}</body></html>`;
    return content;
  };

  // Generate project analytics
  const getProjectAnalytics = () => {
    if (!actions.length) return null;

    const actionResultCount = {
      'successful': 0, 'detected': 0, 'blocked': 0,
      'failed': 0, 'partial': 0, 'unknown': 0,
    };

    const tacticCount = {};
    const timelineData = {};
    const operatorStats = {};
    const targetEngagement = {};

    actions.forEach(action => {
      if (action.result) {
        actionResultCount[action.result] = (actionResultCount[action.result] || 0) + 1;
      }

      if (action.mitreAttackTechnique?.tactic) {
        tacticCount[action.mitreAttackTechnique.tactic] =
          (tacticCount[action.mitreAttackTechnique.tactic] || 0) + 1;
      }

      const week = new Date(action.startTimestamp || action.timestamp).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
      timelineData[week] = (timelineData[week] || 0) + 1;

      if (action.operator) {
        if (!operatorStats[action.operator]) {
          operatorStats[action.operator] = { total: 0, successful: 0 };
        }
        operatorStats[action.operator].total++;
        if (action.result === 'successful') {
          operatorStats[action.operator].successful++;
        }
      }

      // FIX: Handle both populated objects and bare string IDs for targets
      if (action.targets && action.targets.length > 0) {
        action.targets.forEach(targetRef => {
          const target = typeof targetRef === 'object'
            ? targetRef
            : targets.find(t => t._id === targetRef);
          if (target) {
            const key = target.value || target.hostname || target._id;
            if (!targetEngagement[key]) {
              targetEngagement[key] = { total: 0, successful: 0, type: target.type };
            }
            targetEngagement[key].total++;
            if (action.result === 'successful') {
              targetEngagement[key].successful++;
            }
          }
        });
      }
    });

    return {
      actionResultCount,
      tacticCount,
      timelineData,
      operatorStats,
      targetEngagement,
      totalActions: actions.length,
      successRate: ((actionResultCount.successful / actions.length) * 100).toFixed(1),
      detectionRate: ((actionResultCount.detected / actions.length) * 100).toFixed(1),
      averageActionsPerDay: (actions.length / Math.max(1, Object.keys(timelineData).length)).toFixed(1),
      topTechniques: actions
        .filter(a => a.mitreAttackTechnique)
        .reduce((acc, action) => {
          const id = action.mitreAttackTechnique.id;
          acc[id] = (acc[id] || 0) + 1;
          return acc;
        }, {}),
      targetsCompromised: Object.values(targetEngagement).filter(t => t.successful > 0).length
    };
  };

  // Chart configurations
  const getActionResultsChart = (analytics) => ({
    labels: [
      t('actions.successful'), t('actions.detected'), t('actions.blocked'),
      t('actions.failed'), t('actions.partial'), t('actions.unknown')
    ],
    datasets: [{
      data: [
        analytics.actionResultCount.successful, analytics.actionResultCount.detected,
        analytics.actionResultCount.blocked, analytics.actionResultCount.failed,
        analytics.actionResultCount.partial, analytics.actionResultCount.unknown,
      ],
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)', 'rgba(251, 191, 36, 0.8)', 'rgba(239, 68, 68, 0.8)',
        'rgba(156, 163, 175, 0.8)', 'rgba(59, 130, 246, 0.8)', 'rgba(147, 51, 234, 0.8)',
      ],
      borderColor: [
        'rgba(34, 197, 94, 1)', 'rgba(251, 191, 36, 1)', 'rgba(239, 68, 68, 1)',
        'rgba(156, 163, 175, 1)', 'rgba(59, 130, 246, 1)', 'rgba(147, 51, 234, 1)',
      ],
      borderWidth: 1,
    }],
  });

  const getMitreTacticsChart = (analytics) => ({
    labels: Object.keys(analytics.tacticCount),
    datasets: [{
      label: t('reports.mitreTacticsUsage'),
      data: Object.values(analytics.tacticCount),
      backgroundColor: 'rgba(147, 51, 234, 0.6)',
      borderColor: 'rgba(147, 51, 234, 1)',
      borderWidth: 1,
    }],
  });

  const getTimelineChart = (analytics) => {
    const sortedDates = Object.keys(analytics.timelineData).sort((a, b) => new Date(a) - new Date(b));
    return {
      labels: sortedDates,
      datasets: [{
        label: t('reports.actionsTimeline'),
        data: sortedDates.map(date => analytics.timelineData[date]),
        borderColor: 'rgba(59, 130, 246, 1)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: true,
      }],
    };
  };

  const getTargetEngagementChart = (analytics) => {
    const topTargets = Object.entries(analytics.targetEngagement)
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 10);

    return {
      labels: topTargets.map(([target]) => target),
      datasets: [
        {
          label: t('debrief.totalActions'),
          data: topTargets.map(([, data]) => data.total),
          backgroundColor: 'rgba(59, 130, 246, 0.6)',
          borderColor: 'rgba(59, 130, 246, 1)',
          borderWidth: 1,
        },
        {
          label: t('actions.successful'),
          data: topTargets.map(([, data]) => data.successful),
          backgroundColor: 'rgba(34, 197, 94, 0.6)',
          borderColor: 'rgba(34, 197, 94, 1)',
          borderWidth: 1,
        }
      ],
    };
  };

  const dm = isDarkMode;
  const cardCls = dm ? 'bg-gray-800 text-white' : 'bg-white';
  const textMuted = dm ? 'text-gray-400' : 'text-gray-600';
  const textMain = dm ? 'text-white' : 'text-gray-900';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`border px-4 py-3 rounded ${dm ? 'bg-red-900/50 border-red-500 text-red-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
        {error}
      </div>
    );
  }

  if (!project) {
    return (
      <div className={`border px-4 py-3 rounded ${dm ? 'bg-yellow-900/50 border-yellow-500 text-yellow-200' : 'bg-yellow-50 border-yellow-200 text-yellow-700'}`}>
        {t('common.noData')}
      </div>
    );
  }

  const analytics = getProjectAnalytics();

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-2">
        <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 flex items-center text-sm mb-2">
          <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
          {t('reports.backToProject')}
        </Link>
      </div>
      <PageHeader
        title={t('reports.projectReports')}
        subtitle={`${project.name} - ${project.client}`}
        icon={<FontAwesomeIcon icon={faChartBar} />}
      >
        {debrief && (
          <button
            onClick={() => setShowTemplateModal(true)}
            disabled={exporting}
            className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:bg-purple-400 flex items-center"
          >
            <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
            {t('reports.reportTemplates')}
          </button>
        )}
        <Link
          to={`/projects/${projectId}/debrief`}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 flex items-center"
        >
          <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
          {t('reports.viewDebrief')}
        </Link>
      </PageHeader>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className={`${cardCls} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faBullseye} className="text-3xl mr-4 text-blue-600" />
            <div>
              <p className={`text-2xl font-bold ${textMain}`}>{analytics?.totalActions || 0}</p>
              <p className={`${textMuted} text-sm`}>{t('debrief.totalActions')}</p>
            </div>
          </div>
        </div>

        <div className={`${cardCls} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCheckCircle} className="text-3xl mr-4 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-green-600">{analytics?.successRate || 0}%</p>
              <p className={`${textMuted} text-sm`}>{t('reports.successRate')}</p>
            </div>
          </div>
        </div>

        <div className={`${cardCls} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faBullseye} className="text-3xl mr-4 text-orange-600" />
            <div>
              <p className="text-2xl font-bold text-orange-600">{targets.length}</p>
              <p className={`${textMuted} text-sm`}>{t('reports.totalTargets')}</p>
            </div>
          </div>
        </div>

        <div className={`${cardCls} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faTrophy} className="text-3xl mr-4 text-purple-600" />
            <div>
              <p className="text-2xl font-bold text-purple-600">{analytics?.targetsCompromised || 0}</p>
              <p className={`${textMuted} text-sm`}>{t('reports.targetsCompromised')}</p>
            </div>
          </div>
        </div>

        <div className={`${cardCls} rounded-lg shadow-md p-6`}>
          <div className="flex items-center">
            <FontAwesomeIcon icon={faMapMarkedAlt} className="text-3xl mr-4 text-indigo-600" />
            <div>
              <p className="text-2xl font-bold text-indigo-600">{Object.keys(analytics?.tacticCount || {}).length}</p>
              <p className={`${textMuted} text-sm`}>{t('reports.mitreTactics')}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className={`${cardCls} rounded-lg shadow-md overflow-hidden`}>
        <div className={`border-b ${dm ? 'border-gray-700' : 'border-gray-200'}`}>
          <nav className="flex">
            {[
              { id: 'overview', icon: faChartLine, label: t('reports.analyticsOverview') },
              { id: 'mitre', icon: faMapMarkedAlt, label: t('reports.mitreAnalysis') },
              { id: 'timeline', icon: faClock, label: t('reports.timelineAnalysis') },
              { id: 'export', icon: faDownload, label: t('reports.exportData') },
            ].map(tab => (
              <button
                key={tab.id}
                className={`px-6 py-3 text-sm font-medium flex items-center ${
                  activeTab === tab.id
                    ? dm ? 'border-b-2 border-blue-400 text-blue-400 bg-gray-700' : 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                    : dm ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <FontAwesomeIcon icon={tab.icon} className="mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && analytics && (
            <div className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className={`text-lg font-semibold mb-4 flex items-center ${textMain}`}>
                    <FontAwesomeIcon icon={faChartPie} className="mr-2 text-blue-600" />
                    {t('reports.actionResultsDist')}
                  </h3>
                  <div style={{ height: '300px', position: 'relative' }}>
                    <Pie
                      data={getActionResultsChart(analytics)}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { position: 'bottom', labels: { color: dm ? '#d1d5db' : '#374151' } },
                        },
                      }}
                    />
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-4 flex items-center ${textMain}`}>
                    <FontAwesomeIcon icon={faBullseye} className="mr-2 text-orange-600" />
                    {t('reports.topEngagedTargets')}
                  </h3>
                  <div style={{ height: '300px', position: 'relative' }}>
                    <Bar
                      data={getTargetEngagementChart(analytics)}
                      options={{
                        maintainAspectRatio: false,
                        scales: {
                          y: { beginAtZero: true, ticks: { precision: 0, color: dm ? '#9ca3af' : undefined } },
                          x: { ticks: { color: dm ? '#9ca3af' : undefined } }
                        },
                        plugins: {
                          legend: { position: 'bottom', labels: { color: dm ? '#d1d5db' : '#374151' } },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className={`text-lg font-semibold mb-4 flex items-center ${textMain}`}>
                    <FontAwesomeIcon icon={faMapMarkedAlt} className="mr-2 text-purple-600" />
                    {t('reports.mitreTacticsUsage')}
                  </h3>
                  <div style={{ height: '300px', position: 'relative' }}>
                    <Bar
                      data={getMitreTacticsChart(analytics)}
                      options={{
                        maintainAspectRatio: false,
                        scales: {
                          y: { beginAtZero: true, ticks: { precision: 0, color: dm ? '#9ca3af' : undefined } },
                          x: { ticks: { color: dm ? '#9ca3af' : undefined } }
                        },
                        plugins: { legend: { display: false } },
                      }}
                    />
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-4 flex items-center ${textMain}`}>
                    <FontAwesomeIcon icon={faUser} className="mr-2 text-green-600" />
                    {t('reports.operatorPerformance')}
                  </h3>
                  <div className="space-y-3">
                    {Object.entries(analytics.operatorStats)
                      .sort((a, b) => b[1].total - a[1].total)
                      .map(([operator, stats]) => (
                      <div key={operator} className={`p-4 rounded-lg flex justify-between items-center ${dm ? 'bg-gray-700' : 'bg-gray-50'}`}>
                        <div className="flex items-center">
                          <FontAwesomeIcon icon={faUser} className="mr-2 text-blue-600" />
                          <div>
                            <p className={`font-medium ${textMain}`}>{operator}</p>
                            <p className={`text-sm ${textMuted}`}>{stats.total} {t('common.actions').toLowerCase()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">
                            {stats.total > 0 ? ((stats.successful / stats.total) * 100).toFixed(1) : 0}%
                          </p>
                          <p className={`text-xs ${textMuted}`}>{t('reports.successRate')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${textMain}`}>
                  <FontAwesomeIcon icon={faAward} className="mr-2 text-purple-600" />
                  {t('reports.topTechniques')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Object.entries(analytics.topTechniques)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 6)
                    .map(([techniqueId, count]) => {
                      const technique = actions.find(a => a.mitreAttackTechnique?.id === techniqueId)?.mitreAttackTechnique;
                      return (
                        <div key={techniqueId} className={`p-4 rounded-lg ${dm ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                          <div className={`font-medium flex items-center ${dm ? 'text-purple-300' : 'text-purple-800'}`}>
                            <FontAwesomeIcon icon={faMapMarkedAlt} className="mr-2" />
                            {techniqueId}
                          </div>
                          {technique && (
                            <div className={`text-sm mt-1 ${textMuted}`}>{technique.name}</div>
                          )}
                          <div className="text-lg font-bold text-purple-600 mt-2">{count} uses</div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {/* MITRE Tab */}
          {activeTab === 'mitre' && (
            <div>
              <MitreHeatmap
                actions={actions}
                title={`MITRE ATT&CK Coverage - ${project.name}`}
                webFindings={webFindingsForMitre}
              />

              <div className="mt-8">
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${textMain}`}>
                  <FontAwesomeIcon icon={faMapMarkedAlt} className="mr-2 text-purple-600" />
                  {t('reports.techniqueDetails')}
                </h3>
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y ${dm ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={dm ? 'bg-gray-900' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dm ? 'text-gray-400' : 'text-gray-500'}`}>{t('attackChain.technique')}</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dm ? 'text-gray-400' : 'text-gray-500'}`}>{t('common.name')}</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dm ? 'text-gray-400' : 'text-gray-500'}`}>{t('attackChain.tactic')}</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dm ? 'text-gray-400' : 'text-gray-500'}`}>Uses</th>
                        <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${dm ? 'text-gray-400' : 'text-gray-500'}`}>{t('reports.successRate')}</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${dm ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {Object.entries(
                        actions
                          .filter(a => a.mitreAttackTechnique)
                          .reduce((acc, action) => {
                            const id = action.mitreAttackTechnique.id;
                            if (!acc[id]) {
                              acc[id] = { technique: action.mitreAttackTechnique, count: 0, successful: 0 };
                            }
                            acc[id].count++;
                            if (action.result === 'successful') acc[id].successful++;
                            return acc;
                          }, {})
                      )
                        .sort((a, b) => b[1].count - a[1].count)
                        .map(([techniqueId, data]) => (
                        <tr key={techniqueId} className={dm ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${textMain}`}>{techniqueId}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textMuted}`}>{data.technique.name}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textMuted}`}>{data.technique.tactic}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textMuted}`}>{data.count}</td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${textMuted}`}>
                            <div className="flex items-center">
                              <span className="mr-2">{((data.successful / data.count) * 100).toFixed(1)}%</span>
                              <div className={`w-16 rounded-full h-2 ${dm ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(data.successful / data.count) * 100}%` }}></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && analytics && (
            <div>
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${textMain}`}>
                <FontAwesomeIcon icon={faClock} className="mr-2 text-blue-600" />
                {t('reports.actionsTimeline')}
              </h3>
              <div style={{ height: '400px', position: 'relative' }}>
                <Line
                  data={getTimelineChart(analytics)}
                  options={{
                    maintainAspectRatio: false,
                    scales: {
                      y: { beginAtZero: true, ticks: { precision: 0, color: dm ? '#9ca3af' : undefined } },
                      x: { ticks: { color: dm ? '#9ca3af' : undefined } }
                    },
                    plugins: { legend: { display: false } },
                  }}
                />
              </div>

              <div className="mt-8">
                <h3 className={`text-lg font-semibold mb-4 flex items-center ${textMain}`}>
                  <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-green-600" />
                  {t('reports.recentActions')}
                </h3>
                <div className="space-y-3">
                  {actions
                    .sort((a, b) => new Date(b.startTimestamp) - new Date(a.startTimestamp))
                    .slice(0, 10)
                    .map((action) => (
                      <div
                        key={action._id}
                        className={`p-4 rounded-lg cursor-pointer transition-colors ${dm ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`}
                        onClick={() => setSelectedAction(action)}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className={`font-medium flex items-center ${textMain}`}>
                              <FontAwesomeIcon icon={faBullseye} className="mr-2 text-blue-600" />
                              {action.title}
                            </h4>
                            <p className={`text-sm mt-1 flex items-center ${textMuted}`}>
                              <FontAwesomeIcon icon={faCalendarAlt} className="mr-2 text-gray-400" />
                              {new Date(action.startTimestamp).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                              <FontAwesomeIcon icon={faUser} className="ml-3 mr-1 text-gray-400" />
                              {action.operator}
                            </p>
                            {action.mitreAttackTechnique && (
                              <p className="text-sm text-purple-500 mt-1 flex items-center">
                                <FontAwesomeIcon icon={faMapMarkedAlt} className="mr-2" />
                                {action.mitreAttackTechnique.id} - {action.mitreAttackTechnique.name}
                              </p>
                            )}
                          </div>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            action.result === 'successful' ? 'bg-green-100 text-green-800' :
                            action.result === 'detected' ? 'bg-yellow-100 text-yellow-800' :
                            action.result === 'blocked' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {t(`actions.${action.result}`) !== `actions.${action.result}` ? t(`actions.${action.result}`) : action.result}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Export Tab */}
          {activeTab === 'export' && (
            <div className="space-y-6">
              <h3 className={`text-lg font-semibold mb-4 flex items-center ${textMain}`}>
                <FontAwesomeIcon icon={faDownload} className="mr-2 text-blue-600" />
                {t('reports.exportData')}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className={`rounded-lg p-6 ${dm ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-2 flex items-center ${textMain}`}>
                    <FontAwesomeIcon icon={faFilePdf} className="mr-2 text-red-600" />
                    {t('reports.executive')} (PDF)
                  </h4>
                  <p className={`text-sm mb-4 ${textMuted}`}>
                    Generate a professional PDF report with key findings, statistics, and recommendations.
                  </p>
                  <button
                    onClick={handleExportPDF}
                    disabled={exporting}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-blue-400 w-full flex items-center justify-center"
                  >
                    <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                    {t('reports.exportExecSummary')}
                  </button>
                </div>

                <div className={`rounded-lg p-6 ${dm ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-2 flex items-center ${textMain}`}>
                    <FontAwesomeIcon icon={faFileExcel} className="mr-2 text-green-600" />
                    {t('reports.excel')}
                  </h4>
                  <p className={`text-sm mb-4 ${textMuted}`}>
                    Export all project data including actions, targets, and results in Excel format.
                  </p>
                  <button
                    onClick={handleExportExcel}
                    disabled={exporting}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-green-400 w-full flex items-center justify-center"
                  >
                    <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                    {t('reports.exportExcel')}
                  </button>
                </div>

                <div className={`rounded-lg p-6 ${dm ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-2 flex items-center ${textMain}`}>
                    <FontAwesomeIcon icon={faMap} className="mr-2 text-purple-600" />
                    MITRE Navigator Layer
                  </h4>
                  <p className={`text-sm mb-4 ${textMuted}`}>
                    Export MITRE ATT&CK coverage as a Navigator layer JSON file for visualization.
                  </p>
                  <button
                    onClick={handleExportMitreNavigator}
                    disabled={exporting}
                    className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:bg-purple-400 w-full flex items-center justify-center"
                  >
                    <FontAwesomeIcon icon={faMap} className="mr-2" />
                    {t('reports.exportMitreNavigator')}
                  </button>
                </div>

                <div className={`rounded-lg p-6 ${dm ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <h4 className={`font-medium mb-2 flex items-center ${textMain}`}>
                    <FontAwesomeIcon icon={faChartArea} className="mr-2 text-orange-600" />
                    {t('reports.exportAnalytics')}
                  </h4>
                  <p className={`text-sm mb-4 ${textMuted}`}>
                    Export comprehensive analytics data in JSON format for custom analysis.
                  </p>
                  <button
                    onClick={handleExportAnalytics}
                    disabled={exporting}
                    className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 disabled:bg-orange-400 w-full flex items-center justify-center"
                  >
                    <FontAwesomeIcon icon={faChartArea} className="mr-2" />
                    {t('reports.exportAnalytics')}
                  </button>
                </div>
              </div>

              {/* Report Templates Section */}
              {debrief && (
                <div className={`border rounded-lg p-6 mt-6 ${dm ? 'bg-purple-900/20 border-purple-500' : 'bg-purple-50 border-purple-200'}`}>
                  <h4 className={`font-medium mb-2 flex items-center ${dm ? 'text-purple-300' : 'text-purple-800'}`}>
                    <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
                    {t('reports.reportTemplates')}
                  </h4>
                  <p className={`text-sm mb-3 ${dm ? 'text-purple-200' : 'text-purple-700'}`}>
                    {t('reports.chooseTemplate')}
                  </p>
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => setShowTemplateModal(true)}
                      disabled={exporting}
                      className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:bg-purple-400 flex items-center"
                    >
                      <FontAwesomeIcon icon={faFileAlt} className="mr-2" />
                      {t('reports.selectTemplate')}
                    </button>
                    <button
                      onClick={handleExportDebriefHTML}
                      disabled={exporting}
                      className={`px-4 py-2 rounded-md flex items-center ${dm ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
                      {t('reports.comprehensiveDebrief')} (HTML)
                    </button>
                  </div>
                </div>
              )}

              <div className={`border rounded-lg p-4 mt-6 ${dm ? 'bg-indigo-900/30 border-indigo-500' : 'bg-blue-50 border-blue-200'}`}>
                <h4 className={`font-medium mb-2 flex items-center ${dm ? 'text-indigo-300' : 'text-blue-800'}`}>
                  <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
                  {t('reports.viewDebrief')}
                </h4>
                <p className={`text-sm mb-3 ${dm ? 'text-indigo-200' : 'text-blue-700'}`}>
                  {t('reports.comprehensiveDebrief')}
                </p>
                <Link
                  to={`/projects/${projectId}/debrief`}
                  className="inline-flex items-center bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
                >
                  <FontAwesomeIcon icon={faClipboardList} className="mr-2" />
                  {t('reports.viewDebrief')} →
                </Link>
              </div>

              {exporting && (
                <div className={`rounded-lg p-4 text-center ${dm ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-blue-600 mb-2" />
                  <p className={textMuted}>{t('reports.generating')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Report Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('reports.selectTemplate')}</h2>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                >
                  ✕
                </button>
              </div>
              <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('reports.chooseTemplate')}
              </p>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {reportTemplatesService.templates.map(template => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedTemplate === template.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                      : isDarkMode
                        ? 'border-gray-700 hover:border-gray-600 bg-gray-700/50'
                        : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {template.name}
                      </h3>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {template.description}
                      </p>
                      <div className="mt-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                        }`}>
                          {template.audience}
                        </span>
                      </div>
                    </div>
                    {selectedTemplate === template.id && (
                      <div className="text-indigo-600 text-xl">✓</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {selectedTemplate === 'board-presentation' && (
              <div className={`px-6 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{t('reports.presentationTheme')}</span>
                  <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                    <button
                      onClick={() => setReportTheme('light')}
                      className={`px-3 py-1.5 text-sm ${reportTheme === 'light' ? 'bg-indigo-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-700'}`}
                    >
                      Light
                    </button>
                    <button
                      onClick={() => setReportTheme('dark')}
                      className={`px-3 py-1.5 text-sm ${reportTheme === 'dark' ? 'bg-indigo-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-700'}`}
                    >
                      Dark
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className={`p-6 border-t flex justify-end space-x-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowTemplateModal(false)}
                className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                {t('reports.cancel')}
              </button>
              <button
                onClick={() => selectedTemplate && handleTemplateExport(selectedTemplate)}
                disabled={!selectedTemplate || exporting}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed"
              >
                {exporting ? t('reports.generatingReport') : t('reports.generateReport')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        projectId={projectId}
        onActionUpdated={(updated) => setSelectedAction(updated)}
      />
    </div>
  );
};

export default ProjectReports;
